/**
 * ContextualHelpDrawer.tsx
 *
 * WCAG 2.2 AA compliant non-modal in-situ contextual help drawer.
 *
 * Standards implemented:
 * 1. `<aside role="region" aria-labelledby="help-drawer-title">`.
 * 2. Non-modal: Does NOT trap focus, allowing keyboard users to tab naturally
 *    or cycle with F6 / Cmd+?.
 * 3. Minimized ViewHelpContext (routeId, pluginId, allowedCollectionIds).
 * 4. Contextual quickstart guides, keyboard cheat sheet, and live query box.
 */

import React, { useState, useEffect, useRef } from 'react';
import { globalAccessibilityManager } from '../../a11y/accessibility-manager.js';

export interface ViewHelpContext {
  readonly routeId: string;
  readonly pluginId: string;
  readonly allowedCollectionIds?: readonly string[];
  readonly selectedEntityIds?: readonly string[];
}

export interface ContextualHelpDrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly viewContext: ViewHelpContext;
}

const VIEW_HELP_REGISTRY: Record<
  string,
  {
    readonly title: string;
    readonly quickstart: readonly string[];
    readonly shortcuts: readonly { key: string; label: string }[];
    readonly faqs: readonly { q: string; a: string }[];
  }
> = {
  scrum: {
    title: 'Engineering & Sprint Backlog Help (#scrum)',
    quickstart: [
      'Submit daily standup notes to update sprint status.',
      'Click or hover over any #ID task badge (e.g. #88) to inspect full task details.',
      'Filter blocked items by asking the Scrum Copilot.',
    ],
    shortcuts: [
      { key: 'Cmd+K', label: 'Open Universal Command Palette' },
      { key: 'F6', label: 'Cycle focus between workspace and Help Drawer' },
      { key: '?', label: 'Toggle Contextual Help Drawer' },
      { key: 'Esc', label: 'Dismiss active drawer or hover card' },
    ],
    faqs: [
      {
        q: 'How do I resolve a task blocker?',
        a: 'Open the task drawer by clicking its #ID badge and select "Propose Unblock Plan" or link an LLMWiki runbook.',
      },
      {
        q: 'Why are standup updates showing as activity ribbons?',
        a: 'Routine notes are converted to ambient status ribbons to keep the chat timeline clean and uncluttered.',
      },
    ],
  },
  crm: {
    title: 'Sales Pipeline & Revenue Help (#crm)',
    quickstart: [
      'Track deal stages from Discovery to Closed-Won.',
      'Hover over $DEAL badges for real-time win probability and deal size.',
      'Approve Next-Best-Action cards to draft quotes or schedule follow-ups.',
    ],
    shortcuts: [
      { key: 'Cmd+K', label: 'Open Universal Command Palette' },
      { key: 'F6', label: 'Cycle focus between workspace and Help Drawer' },
      { key: '?', label: 'Toggle Contextual Help Drawer' },
    ],
    faqs: [
      {
        q: 'What triggers deal win celebrations?',
        a: 'Transitioning any deal card into the "Closed Won" stage triggers celebratory feedback and revenue ledger updates.',
      },
    ],
  },
  wiki: {
    title: 'Knowledge Wiki & Runbooks Help (#wiki)',
    quickstart: [
      'Search documentation and technical runbooks across verified mesh collections.',
      'Upload markdown guides with scoped RAG ingestion.',
      'Inspect citations grounded in verifiable knowledge hashes.',
    ],
    shortcuts: [
      { key: 'Cmd+K', label: 'Open Universal Command Palette' },
      { key: 'F6', label: 'Cycle focus between workspace and Help Drawer' },
    ],
    faqs: [
      {
        q: 'How does RAG scoping work?',
        a: 'Documents are scoped into Global, Plugin, or Private Channel partitions to guarantee zero cross-scope data leakage.',
      },
    ],
  },
};

export const ContextualHelpDrawer: React.FC<ContextualHelpDrawerProps> = ({
  isOpen,
  onClose,
  viewContext,
}) => {
  const [askQuery, setAskQuery] = useState<string>('');
  const [assistantReply, setAssistantReply] = useState<string | null>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const helpData = VIEW_HELP_REGISTRY[viewContext.routeId] || VIEW_HELP_REGISTRY.scrum;

  useEffect(() => {
    if (isOpen) {
      globalAccessibilityManager.announce(`Help Drawer opened for ${helpData.title}.`, 'polite');
    }
  }, [isOpen, helpData.title]);

  // Global F6 landmark navigation listener to cycle focus into drawer
  useEffect(() => {
    const handleGlobalKey = (e: KeyboardEvent) => {
      if ((e.key === 'F6' || (e.metaKey && e.key === '?')) && isOpen && drawerRef.current) {
        e.preventDefault();
        const firstFocusable = drawerRef.current.querySelector<HTMLElement>(
          'button, input, [tabindex="0"]'
        );
        firstFocusable?.focus();
        globalAccessibilityManager.announce('Focus moved to Contextual Help Drawer.', 'polite');
      }
    };
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [isOpen]);

  const handleAskAssistant = (e: React.FormEvent) => {
    e.preventDefault();
    if (!askQuery.trim()) return;

    // Simulate grounded in-situ response
    setAssistantReply(
      `Based on the ${viewContext.routeId.toUpperCase()} context, you can access your resources via the command palette or workspace controls.`
    );
    globalAccessibilityManager.announce('Assistant reply ready in help drawer.', 'polite');
    setAskQuery('');
  };

  if (!isOpen) return null;

  return (
    <aside
      ref={drawerRef}
      role="region"
      aria-labelledby="help-drawer-title"
      style={{
        position: 'fixed',
        top: 'var(--app-header-height, 48px)',
        right: 0,
        bottom: 'var(--app-footer-height, 40px)',
        width: '380px',
        maxWidth: '90vw',
        backgroundColor: 'var(--theme-bg-modal, #0f172a)',
        borderLeft: '1px solid var(--theme-border-highlight, #38bdf8)',
        boxShadow: 'var(--theme-shadow-lg, -10px 0 25px rgba(0,0,0,0.5))',
        zIndex: 40,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Drawer Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--theme-border, #1e293b)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'var(--theme-bg-header, #0f172a)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px', color: 'var(--theme-accent, #38bdf8)' }}>📖</span>
          <h2
            id="help-drawer-title"
            style={{
              fontSize: 'var(--theme-text-base, 14px)',
              fontWeight: 600,
              color: 'var(--theme-text-primary, #f8fafc)',
            }}
          >
            Contextual Help
          </h2>
        </div>
        <button
          className="icon-btn"
          aria-label="Close help drawer"
          onClick={onClose}
          style={{
            minWidth: 'var(--a11y-target-min, 24px)',
            minHeight: 'var(--a11y-target-min, 24px)',
            cursor: 'pointer',
          }}
        >
          ✕
        </button>
      </div>

      {/* Drawer Scrollable Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        {/* Quickstart Section */}
        <div>
          <h3
            style={{
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 700,
              color: 'var(--theme-accent, #38bdf8)',
              marginBottom: '8px',
            }}
          >
            {helpData.title}
          </h3>
          <ul style={{ paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {helpData.quickstart.map((item, i) => (
              <li
                key={i}
                style={{
                  fontSize: 'var(--theme-text-sm, 13px)',
                  color: 'var(--theme-text-secondary, #cbd5e1)',
                  lineHeight: '1.4',
                }}
              >
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Keyboard Shortcuts Section */}
        <div>
          <h3
            style={{
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 700,
              color: 'var(--theme-text-muted, #94a3b8)',
              marginBottom: '8px',
            }}
          >
            Keyboard Shortcuts
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {helpData.shortcuts.map((sc, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 10px',
                  backgroundColor: 'var(--theme-bg-card, #151d2a)',
                  borderRadius: '6px',
                  border: '1px solid var(--theme-border, #1e293b)',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    color: 'var(--theme-text-secondary, #cbd5e1)',
                  }}
                >
                  {sc.label}
                </span>
                <kbd
                  style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    backgroundColor: 'var(--theme-bg-main, #0b0f19)',
                    border: '1px solid var(--theme-border, #1e293b)',
                    borderRadius: '4px',
                    color: 'var(--theme-accent, #38bdf8)',
                    fontFamily: 'var(--theme-font-mono)',
                  }}
                >
                  {sc.key}
                </kbd>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs Section */}
        <div>
          <h3
            style={{
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 700,
              color: 'var(--theme-text-muted, #94a3b8)',
              marginBottom: '8px',
            }}
          >
            Frequently Asked Questions
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {helpData.faqs.map((faq, i) => (
              <div
                key={i}
                style={{
                  padding: '10px',
                  backgroundColor: 'var(--theme-bg-card, #151d2a)',
                  borderRadius: '8px',
                  border: '1px solid var(--theme-border, #1e293b)',
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'var(--theme-text-primary, #f8fafc)',
                    marginBottom: '4px',
                  }}
                >
                  {faq.q}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: 'var(--theme-text-secondary, #cbd5e1)',
                    lineHeight: '1.4',
                  }}
                >
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ask Assistant Box */}
        <div
          style={{
            padding: '14px',
            backgroundColor: 'var(--theme-bg-subtle, #020617)',
            borderRadius: '8px',
            border: '1px solid var(--theme-border-highlight, #38bdf8)',
          }}
        >
          <div
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--theme-accent, #38bdf8)',
              marginBottom: '8px',
            }}
          >
            Ask Copilot about this View
          </div>
          <form onSubmit={handleAskAssistant} style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              placeholder="Ask a question about this screen..."
              value={askQuery}
              onChange={(e) => setAskQuery(e.target.value)}
              style={{
                flex: 1,
                padding: '6px 10px',
                fontSize: '12px',
                backgroundColor: 'var(--theme-bg-input, #1e293b)',
                border: '1px solid var(--theme-border, #1e293b)',
                borderRadius: '4px',
                color: 'var(--theme-text-primary, #f8fafc)',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: 'var(--theme-accent, #38bdf8)',
                color: 'var(--theme-text-inverse, #0b0f19)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Ask
            </button>
          </form>
          {assistantReply && (
            <div
              style={{
                marginTop: '10px',
                padding: '8px 10px',
                backgroundColor: 'var(--theme-bg-card, #151d2a)',
                borderRadius: '4px',
                fontSize: '12px',
                color: 'var(--theme-text-primary, #f8fafc)',
              }}
            >
              🤖 {assistantReply}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};
